import React, { useCallback, useEffect, useMemo, useState } from 'react';
import Carousel, { Modal, ModalGateway } from 'react-images';
import { useQueryClient, useMutation } from 'react-query';

import ParameterFilter from './Filter/ParameterFilter';
// @ts-expect-error Will convert later
import MapBox from '../ContentBox/MapBox';
import Card from '../Card/Card';

import { UNSEEN } from '../BulletinView/constantes';
import { useBulletin, useBulletinResponse } from '../../context/bulletin';
import SendMessageButton from '../BulletinView/SendMessageButton';
import FlagModal from '../ContentBox/FlagModal';
import { BulletinGraph, BulletinVariable } from '../BulletinView/types';
import { ParametersObject } from './Filter/helpers';

type MapStatus = Record<
  string,
  {
    isUnseen: boolean;
    isFlagged: boolean;
  }
>;

interface VariableViewProps {
  disableFlag: boolean;
  setMapsAsSeen: (mapsId: number[] | ((prev: number[]) => number[])) => void;
  updateEntryStatus: (
    entry: BulletinVariable,
    status: { isFlagged?: boolean; isUnseen?: boolean },
  ) => void;
  userIsAuthenticated: boolean;
  variableTreeKeyToDisplay: string;
  variableToDisplay: BulletinVariable;
  nextEntryLink: string | null;
  prevEntryLink: string | null;
}

const VariableView = ({
  disableFlag,
  setMapsAsSeen,
  updateEntryStatus,
  userIsAuthenticated,
  variableTreeKeyToDisplay,
  variableToDisplay,
  nextEntryLink,
  prevEntryLink,
}: VariableViewProps) => {
  const [flagModalVisible, setFlagModalVisible] = useState(false);
  const [flagged, setFlagged] = useState(variableToDisplay.flag === true);
  const [filterParameters, setFilterParameters] =
    useState<ParametersObject | null>(null);
  const [selectedIndex, setSelectedIndex] = useState<number | undefined>();
  const [modalIsOpen, setModal] = useState(false);
  const [mapsAreSeen, setMapsAreSeen] = useState<number[]>([]);
  const queryClient = useQueryClient();

  const { bulletinType, bulletinDate } = useBulletin();
  const bulletinResponse = useBulletinResponse();

  const { mutate: mutateMapsAsSeen, isLoading } = useMutation<
    void,
    unknown,
    number[]
  >(async mapsId => setMapsAsSeen(mapsId), {
    onSuccess: (_response, mapsId) => {
      queryClient.invalidateQueries({
        queryKey: ['BulletinQuery', bulletinType, bulletinDate],
      });

      queryClient.invalidateQueries({
        queryKey: [
          'BulletinVariableQuery',
          bulletinType,
          variableTreeKeyToDisplay,
          bulletinDate,
        ],
      });
      setMapsAreSeen(prevMapsAreSeen => [...prevMapsAreSeen, ...mapsId]);
    },
  });

  const mapStatus: MapStatus = useMemo(() => {
    const nextMapStatus: MapStatus = {};
    variableToDisplay.graphs.forEach(({ id, status, flagged: f }) => {
      nextMapStatus[id] = {
        isUnseen: status === UNSEEN,
        isFlagged: Boolean(f),
      };
    });
    return nextMapStatus;
  }, [variableToDisplay]);

  const getFilteredGraphs = (
    graphs: BulletinGraph[],
    filters: ParametersObject,
  ) =>
    graphs.filter(graph =>
      Object.entries(filters).every(
        ([key, value]) => graph[key as keyof ParametersObject] === value,
      ),
    );

  const filterGraphs = useCallback(
    (bulletin: BulletinVariable): BulletinGraph[][] => {
      const { graphs } = bulletin;
      const filters = filterParameters || {};
      const filteredGraphs = getFilteredGraphs(graphs, filters);
      const mensualFilteredGraphs = getFilteredGraphs(graphs, {
        ...filters,
        diagnostic_type: 'clim-RMSDobs',
      });
      const nextGraphs = [...filteredGraphs, ...mensualFilteredGraphs]

        // the sort function works because : 'FCST0' < 'FCST1' < 'FCST3'
        .sort((a, b) => a.leadtime.localeCompare(b.leadtime));

      if (!nextGraphs.some(graph => graph.metric_type === 'CLASS4')) {
        return nextGraphs.map(item => [item]);
      }

      // We need to groupBy CLASS
      return nextGraphs.reduce((list, item, index) => {
        if (index % 2 === 0) {
          list.push([item]);
        } else {
          list[list.length - 1].push(item);
        }
        return list;
      }, [] as BulletinGraph[][]);
    },
    [filterParameters],
  );

  useEffect(() => {
    // when maps are displayed, mark automatically all unseen maps as seen
    if (userIsAuthenticated) {
      if (filterParameters === null) {
        return;
      }
      const nextMapStatus = filterGraphs(variableToDisplay)
        .flat()
        .flatMap(item => {
          // get item state from local state mapStatus (the map can be flagged)
          if (item.status === UNSEEN && !mapsAreSeen.includes(item.id)) {
            return item.id;
          }
          return [];
        });
      if (nextMapStatus.length > 0 && !isLoading) {
        mutateMapsAsSeen(nextMapStatus);
      }
    }
  }, [
    filterGraphs,
    filterParameters,
    mapsAreSeen,
    mutateMapsAsSeen,
    isLoading,
    userIsAuthenticated,
    variableToDisplay,
  ]);

  const toggleImageModal = useCallback((index?: number) => {
    setModal(boolean => !boolean);
    setSelectedIndex(index);
  }, []);

  const setVariableFlagged = (
    variable: BulletinVariable,
    isFlagged: boolean,
  ) => {
    if (isFlagged) {
      updateEntryStatus(variable, { isFlagged: true });
    } else {
      updateEntryStatus(variable, { isUnseen: false });
    }

    queryClient.invalidateQueries({
      queryKey: [
        'BulletinVariableQuery',
        bulletinType,
        variableTreeKeyToDisplay,
        bulletinDate,
      ],
    });
  };

  return (
    <div>
      <div
        style={{
          display: 'flex',
          flexDirection: 'row',
          alignItems: 'center',
          gap: 10,
        }}
      >
        <h2>{variableToDisplay.name}</h2>
        {userIsAuthenticated && !disableFlag && (
          <SendMessageButton
            alreadySent={flagged}
            onClick={() => setFlagModalVisible(true)}
            type="entry"
            itemId={variableToDisplay.id}
          />
        )}
      </div>
      <ParameterFilter
        variable={variableToDisplay}
        update={setFilterParameters}
        filterParameters={filterParameters || {}}
        nextEntryLink={nextEntryLink}
        prevEntryLink={prevEntryLink}
        isLoading={isLoading}
      />

      {(variableToDisplay.outdated ||
        variableToDisplay.graphs.length === 0) && (
        <Card className="alert-danger warningCard">
          <div className="text-center">
            {variableToDisplay.outdated &&
              variableToDisplay.graphs.length !== 0 && (
                <>The maps displayed for this variable are outdated!</>
              )}
            {variableToDisplay.graphs.length === 0 && (
              <>Missing maps on the {bulletinDate?.replaceAll('-', '/')}</>
            )}
          </div>
        </Card>
      )}

      <div
        className={`grid grid-cols-${filterGraphs(variableToDisplay).length}`}
      >
        {filterParameters &&
          filterGraphs(variableToDisplay).map((row, rowIndex) => {
            let nextIndex = rowIndex;
            if (row.length === 2) {
              nextIndex = rowIndex + Number(rowIndex > 0) * rowIndex;
            }
            return (
              <div key={rowIndex}>
                {row.map((graph, index) => (
                  <MapBox
                    key={graph.id}
                    content={graph}
                    index={nextIndex + index}
                    toggleImageModal={toggleImageModal}
                    updateFlag={setVariableFlagged}
                    variable={variableToDisplay}
                    status={mapStatus[graph.id]}
                    disableFlag={!userIsAuthenticated || disableFlag}
                    userIsAuthenticated={userIsAuthenticated}
                    validationEnabled={
                      bulletinResponse.bulletin.validation_enabled
                    }
                  />
                ))}
              </div>
            );
          })}
      </div>

      <ModalGateway>
        {modalIsOpen && (
          <Modal onClose={() => toggleImageModal()}>
            <Carousel
              views={filterGraphs(variableToDisplay)
                .flat()
                .map(g => ({
                  source: g.picture,
                  caption: g.name,
                }))}
              currentIndex={selectedIndex}
            />
          </Modal>
        )}
      </ModalGateway>
      <FlagModal
        show={flagModalVisible}
        type="entry"
        id={variableToDisplay.tree_key}
        onCancel={() => setFlagModalVisible(false)}
        onSuccess={() => {
          setFlagModalVisible(false);
          setFlagged(true);
        }}
      />
    </div>
  );
};

export default VariableView;
